<template>
    <div>
        <div class="header mt-5 mb-5">
            {{ $vuetify.lang.t('$vuetify.punchLog.header')}}
        </div>
        <div class="action-header mb-3">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.punchLog.searchText')"
                        solo dense hide-details
                        height="40px"
                        @keyup.enter="searchPunchLog()">
                    </v-text-field>
                    <v-btn
                        class="ml-3"
                        height="40px"
                        color="primary"
                        outlined
                        @click="searchPunchLog()">
                        {{ $vuetify.lang.t('$vuetify.punchLog.search') }}
                    </v-btn>
                </div>
            </div>
            <div class="d-flex ml-3">
                <v-btn
                    class="mr-3 btn-text-color"
                    height="50"
                    width="200"
                    @click="genPdfReport()"
                    color="#ff544a">
                    {{ $vuetify.lang.t('$vuetify.punchLog.exportPDF') }}
                </v-btn>
                <v-btn
                    class="btn-text-color"
                    height="50"
                    width="200"
                    @click="genExcelReport()"
                    color="#139888">
                    {{ $vuetify.lang.t('$vuetify.punchLog.exportExcel') }}
                </v-btn>
            </div>
        </div>
        <div class="action-header">
            <div class="d-flex-wrap">
                <div class="date-range-input">
                    <date-picker-range
                        v-model="dateRange" />
                </div>
                <div class="ml-3 select-filter">
                    <v-select
                        @change="searchPunchLog()"
                        v-model="typeFilter"
                        :items="typeList"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        height="40"
                        background-color="#ffffff">
                    </v-select>
                </div>
                <div class="ml-3 select-filter">
                    <v-select
                        @change="searchPunchLog()"
                        v-model="locationFilter"
                        :items="locationList"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        height="40"
                        background-color="#ffffff">
                    </v-select>
                </div>
            </div>
        </div>
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalPunchLog"
                :items="punchLogs"
                :loading="loading">
                <template v-slot:[`item.f_path`]="{ item }">
                    <span v-if="item.f_path">
                        <v-avatar class="pointer" size="35">
                            <img :src="getEmployeeImage(item.f_path)" @click="searchByImage(item.employeeId)">
                        </v-avatar>
                    </span>
                </template>
                <template v-slot:[`item.snap_f_path`]="{ item }">
                    <div class="show-image">
                        <div :class="imageStyle(item)">
                            <i class="far fa-portrait" @click="showImage(item.snap_f_path)"></i>
                        </div>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <div
            v-if="pageCount"
            class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <image-preview
            ref="imagePreview" />
    </div>
</template>

<script>
import DatePickerRange from '@/components/DatePickerRange.vue'
import ImagePreview from '@/components/ImagePreview'

export default {
    name: 'PunchLog',

    components: {
        DatePickerRange,
        ImagePreview
    },

    data () {
        return {
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            loading: false,
            totalPunchLog: 0,
            punchLogs: [],
            dateRange: [this.$dateUtil.getCurrentDate(), this.$dateUtil.getCurrentDate()],
            typeFilter: null,
            locationFilter: null,
            typeList: [],
            locationList: []
        }
    },

    computed: {
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.punchLog.type'), value: 'type', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.employeeId'), value: 'employeeId', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.name'), value: 'fullname', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.imageSearch'), value: 'f_path', width: '15%', align: 'center', sortable: false },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.date'), value: 'date', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.time'), value: 'time', width: '8%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.image'), value: 'snap_f_path', width: '8%', align: 'center', sortable: false },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.location'), value: 'device_name', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.temperature'), value: 'temperature', width: '8%', sortable: false }
            ]
        }
    },

    methods: {
        async initPunchLog () {
            await this.getPunchLogType()
            await this.getLocation()
            await this.getPunchLogs()
        },

        async searchPunchLog () {
            this.options.page = 1
            await this.getPunchLogs()
        },

        async getPunchLogs () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `punchlogs?page=${page}&itemsPerPage=${itemsPerPage}&method=FAC`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            if (this.dateRange.length > 0) {
                url += `&dateRange=${JSON.stringify(this.dateRange)}`
            }

            if (this.typeFilter) {
                url += `&type=${this.typeFilter}`
            }

            if (this.locationFilter) {
                url += `&location=${this.locationFilter}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.punchLogs = res.data.data
                this.totalPunchLog = res.data.total
            }
            this.loading = false
        },

        async getPunchLogType () {
            const res = await this.axios.get('punchlog/type/options')
            if (res && res.data && res.data.status === 'ok') {
                this.typeList = res.data.data
            }
        },

        async getLocation () {
            const res = await this.axios.get('punchlog/location/options')
            if (res && res.data && res.data.status === 'ok') {
                this.locationList = res.data.data
            }
        },

        getEmployeeImage (path) {
            return path || null
        },

        imageStyle (path) {
            return path === null || path === undefined ? 'image-disable' : 'image-enable'
        },

        showImage (path) {
            this.$refs.imagePreview.toggleShow(path)
        },

        searchByImage (employeeId) {
            if (employeeId) {
                this.search = employeeId
                this.searchPunchLog()
                this.search = ''
            }
        },

        async genPdfReport () {
            const form = {
                search: this.search,
                dateRange: this.dateRange,
                type: this.typeFilter,
                method: 'FAC',
                location: this.locationFilter
            }
            localStorage.formData = JSON.stringify(form)
            const routeData = this.$router.resolve({ name: 'ReportNVRPunchLogPDF' })
            window.open(routeData.href)
        },

        async genExcelReport () {
            await this.$store.dispatch('setLoading', true)
            const response = await this.axios.post(
                'nvr-punchlog/export/excel',
                {
                    search: this.search,
                    dateRange: this.dateRange,
                    type: this.typeFilter,
                    method: 'FAC',
                    location: this.locationFilter
                }, {
                    headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                    responseType: 'arraybuffer'
                })

            if (response && response.data) {
                const dateString = this.$dateUtil.getCurrentDate()
                this.$utils.downloadExcelFile(response.data, `nvr-punch-log-report-${dateString}.xlsx`)
            } else {
                await this.$store.dispatch('setErrorModalShow', true)
                await this.$store.dispatch('setErrorModalMsg', this.$vuetify.lang.t('$vuetify.punchLog.somethingWrong'))
            }
            await this.$store.dispatch('setLoading', false)
        }
    },

    watch: {
        options: {
            async handler () {
                await this.initPunchLog()
            },
            deep: true
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.initPunchLog()
            },
            deep: true
        },
        dateRange () {
            if (this.dateRange.length === 0 || this.dateRange.length === 2) {
                this.getPunchLogs()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    .search-container {
        ::v-deep .v-input__control > .v-input__slot {
            min-height: auto !important;
            display: flex !important;
            align-items: center !important;
            box-shadow: none !important;
            border: 1px solid #a8a8a8 !important;
            width: 340px;
        }

        ::v-deep .search-input {
            align-items: center;
            .v-input__control > .v-input__slot {
                width: 340px;
            }
        }
    }

    ::v-deep .select-filter {
        .v-input__control > .v-input__slot {
            border: none !important;
            width: 200px;
        }

        .v-select__selection{
            line-height: 1.75;
        }
    }

    ::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
        margin: 0px !important;
    }

    .btn-text-color {
        color: #ffffff;
    }

    .d-flex-wrap {
        display: flex;
        flex-wrap: wrap;
    }

    .date-range-input {
        width: 300px;
    }

    .select-filter {
        width: 200px;
    }

    .show-image {
        align-items: center;

        .image-enable i {
            cursor: pointer;
            color: #d76436 !important;
            font-weight: 500 !important;
            font-size: 16px;
        }

        .image-disable i {
            pointer-events: none;
            cursor: not-allowed;
            color: #a8a8a8 !important;
            font-weight: 300 !important;
            font-size: 16px;
        }
    }
</style>
